// Комментарий на русском: система сбора ошибок фронтенда.
// Зачем: админ может видеть ошибки пользователей в реальном времени (вкладка "Ошибки" в поддержке).

import { getAccessToken } from '@/lib/authStorage';
import { getClientEnv } from '@/utils/clientEnv';

// Комментарий на русском: типы ошибок для категоризации.
export type ErrorCategory = 'voice' | 'upload' | 'auth' | 'network' | 'ui' | 'api' | 'unknown';

export interface FrontendError {
  category: ErrorCategory;
  message: string;
  details?: string;
  url?: string;
  userAgent?: string;
  clientEnv?: Record<string, unknown>;
  timestamp?: string;
  stack?: string;
}

// Комментарий на русском: очередь ошибок для батчевой отправки.
const errorQueue: FrontendError[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const FLUSH_INTERVAL = 5000; // 5 секунд
const MAX_QUEUE_SIZE = 20;

// Комментарий на русском: отправляем ошибки на сервер.
async function flushErrors(): Promise<void> {
  if (errorQueue.length === 0) return;

  const errors = errorQueue.splice(0, MAX_QUEUE_SIZE);
  const token = getAccessToken();

  try {
    const apiUrl =
      typeof window !== 'undefined'
        ? `${window.location.origin}/api/errors/`
        : '/api/errors/';

    await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ errors }),
    });
  } catch {
    // Комментарий на русском: не падаем, если не удалось отправить ошибки.
    console.warn('[ErrorLogger] Failed to send errors to server');
  }
}

// Комментарий на русском: запускаем таймер для батчевой отправки.
function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushErrors();
  }, FLUSH_INTERVAL);
}

// Комментарий на русском: основная функция логирования ошибки.
export function logError(
  category: ErrorCategory,
  message: string,
  details?: string | Record<string, unknown>,
  error?: Error
): void {
  // Комментарий на русском: всегда выводим в консоль для локальной отладки.
  console.error(`[${category.toUpperCase()}]`, message, details, error);

  const env = (() => {
    try {
      const e = getClientEnv();
      return {
        os: e.os,
        browser: e.browser,
        device: e.device,
        isStandalone: e.isStandalone,
        isTouch: e.isTouch,
      };
    } catch {
      return undefined;
    }
  })();

  const detailsStr = (() => {
    try {
      if (typeof details === 'string') return JSON.stringify({ text: details, clientEnv: env });
      if (details && typeof details === 'object') return JSON.stringify({ ...(details as any), clientEnv: env });
      return env ? JSON.stringify({ clientEnv: env }) : undefined;
    } catch {
      return typeof details === 'string' ? details : JSON.stringify(details);
    }
  })();

  const entry: FrontendError = {
    category,
    message,
    details: detailsStr,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    clientEnv: env,
    timestamp: new Date().toISOString(),
    stack: error?.stack,
  };

  errorQueue.push(entry);

  // Комментарий на русском: если очередь большая — отправляем сразу.
  if (errorQueue.length >= MAX_QUEUE_SIZE) {
    void flushErrors();
  } else {
    scheduleFlush();
  }
}

// Комментарий на русском: хелперы для конкретных типов ошибок.
export function logVoiceError(message: string, details?: string | Record<string, unknown>, error?: Error): void {
  logError('voice', message, details, error);
}

export function logUploadError(message: string, details?: string | Record<string, unknown>, error?: Error): void {
  logError('upload', message, details, error);
}

export function logNetworkError(message: string, details?: string | Record<string, unknown>, error?: Error): void {
  logError('network', message, details, error);
}

export function logApiError(message: string, details?: string | Record<string, unknown>, error?: Error): void {
  logError('api', message, details, error);
}

// Комментарий на русском: глобальный обработчик необработанных ошибок.
export function setupGlobalErrorHandler(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event) => {
    logError('unknown', event.message || 'Unhandled error', {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = reason?.message || String(reason) || 'Unhandled promise rejection';
    logError('unknown', message, { reason: String(reason) });
  });
}

// Комментарий на русском: принудительная отправка (при закрытии страницы).
export function flushErrorsSync(): void {
  if (errorQueue.length === 0) return;
  const errors = errorQueue.splice(0);
  const token = getAccessToken();
  const apiUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/api/errors/`
      : '/api/errors/';

  // Комментарий на русском: sendBeacon для отправки при закрытии страницы.
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    const blob = new Blob([JSON.stringify({ errors, token })], { type: 'application/json' });
    navigator.sendBeacon(apiUrl, blob);
  }
}

// Комментарий на русском: регистрируем обработчик beforeunload.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flushErrorsSync);
}

